import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FileDashed, Plus, UploadSimple, X, CalendarBlank, Spinner } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { dmsApi, fmtDate } from './dmsApi';
import { folderOptions } from './folderTree';

/** 不足書類（書類リクエスト） — placeholder work items filled by upload. */
export default function DocRequests({ meta, folders, onChanged }) {
  const [requests, setRequests] = useState([]);
  const [statusF, setStatusF] = useState('open');
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [fulfilling, setFulfilling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRequests(await dmsApi.requests(statusF)); }
    catch (e) { if (e?.response?.status !== 403) toast.error(e?.response?.data?.detail || '依頼の取得に失敗しました'); }
    finally { setLoading(false); }
  }, [statusF]);
  useEffect(() => { load(); }, [load]);

  const fulfill = async (rid, file) => {
    if (!file) return;
    setFulfilling(rid);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await dmsApi.fulfillRequest(rid, fd);
      toast.success(`「${r.document.title}」を提出しました（レビュー中）`);
      load(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '提出に失敗しました');
    } finally { setFulfilling(null); }
  };

  const cancel = async (rid) => {
    try {
      await dmsApi.cancelRequest(rid);
      toast.success('依頼をキャンセルしました');
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'キャンセルに失敗しました'); }
  };

  return (
    <div data-testid="dms-requests-view">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5">
            <FileDashed size={18} weight="duotone" className="text-violet-600" /> 未提出書類（リクエスト）
          </p>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="dms-req-filter"
            className="h-8 rounded-md border border-zinc-300 bg-white px-2 text-xs outline-none">
            <option value="open">オープン</option>
            <option value="fulfilled">提出済</option>
            <option value="cancelled">キャンセル</option>
            <option value="all">すべて</option>
          </select>
        </div>
        {meta.can_edit && (
          <Button size="sm" onClick={() => setNewOpen(true)} className="h-8 text-xs bg-teal-700 hover:bg-teal-800"
            data-testid="dms-req-new-btn">
            <Plus size={13} className="mr-1" /> 書類をリクエスト
          </Button>
        )}
      </div>

      {loading ? (
        <p className="py-16 text-center text-sm text-zinc-400">読み込み中...</p>
      ) : requests.length === 0 ? (
        <div className="py-16 text-center text-zinc-400" data-testid="dms-req-empty">
          <FileDashed size={40} weight="duotone" className="mx-auto mb-2" />
          <p className="text-sm">リクエストはありません</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dms-req-grid">
          {requests.map((r) => (
            <div key={r.id}
              className={`rounded-xl border-2 border-dashed p-3.5 bg-white ${r.overdue ? 'border-rose-300' : r.status === 'open' ? 'border-violet-200' : 'border-zinc-200 opacity-70'}`}
              data-testid={`dms-req-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-bold text-zinc-800">{r.title}</p>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0 ${
                  r.status === 'open' ? 'bg-violet-50 text-violet-700 border border-violet-200'
                    : r.status === 'fulfilled' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-zinc-100 text-zinc-500 border border-zinc-200'}`}>
                  {r.status === 'open' ? '募集中' : r.status === 'fulfilled' ? '提出済' : 'キャンセル'}
                </span>
              </div>
              {r.description && <p className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{r.description}</p>}
              <div className="text-[10px] text-zinc-400 mt-1.5 space-y-0.5">
                <p>{r.doc_type_label} → {r.folder_name}</p>
                <p>依頼者: {r.created_by}{r.assignee_name ? ` → 担当: ${r.assignee_name}` : ''}</p>
                {r.due_date && (
                  <p className={`inline-flex items-center gap-1 ${r.overdue ? 'text-rose-600 font-bold' : ''}`}
                    data-testid={`dms-req-due-${r.id}`}>
                    <CalendarBlank size={10} /> 期限 {r.due_date}{r.overdue ? '（期限超過）' : ''}
                  </p>
                )}
                {r.status === 'fulfilled' && <p>提出: {r.fulfilled_by}（{fmtDate(r.fulfilled_at)}）</p>}
              </div>
              {r.status === 'open' && meta.can_edit && (
                <div className="flex items-center gap-1.5 mt-2.5">
                  <label className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-[11px] font-semibold cursor-pointer transition-colors">
                    {fulfilling === r.id ? <Spinner size={13} className="animate-spin" /> : <UploadSimple size={13} />}
                    アップロードで応える
                    <input type="file" className="hidden" data-testid={`dms-req-fulfill-${r.id}`}
                      onChange={(e) => { fulfill(r.id, e.target.files?.[0]); e.target.value = ''; }}
                      disabled={fulfilling === r.id} />
                  </label>
                  <button type="button" onClick={() => cancel(r.id)}
                    className="h-8 px-2 rounded-md border border-zinc-200 text-zinc-400 hover:text-rose-600 hover:border-rose-300"
                    title="キャンセル" data-testid={`dms-req-cancel-${r.id}`}>
                    <X size={13} weight="bold" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {newOpen && (
        <NewRequestDialog meta={meta} folders={folders}
          onClose={() => setNewOpen(false)}
          onSaved={() => { setNewOpen(false); load(); onChanged?.(); }} />
      )}
    </div>
  );
}

function NewRequestDialog({ meta, folders, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [folderId, setFolderId] = useState(folders[0]?.id || '');
  const [docType, setDocType] = useState('other');
  const [dueDate, setDueDate] = useState('');
  const [assignee, setAssignee] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error('タイトルを入力してください'); return; }
    setSaving(true);
    try {
      await dmsApi.createRequest({
        title: title.trim(), description: description.trim() || null,
        folder_id: folderId, doc_type: docType,
        due_date: dueDate || null, assignee_name: assignee.trim() || null,
      });
      toast.success('書類リクエストを作成しました');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '作成に失敗しました');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="dms-req-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileDashed size={20} weight="duotone" className="text-violet-600" /> 書類リクエスト
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-600">求める書類 <span className="text-rose-500">＊</span></Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
              placeholder="例: A社向け 基板図面 Rev.2" className="h-9 mt-1" data-testid="dms-req-title" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">説明（任意）</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000}
              className="h-9 mt-1" data-testid="dms-req-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">保存先フォルダー</Label>
              <select value={folderId} onChange={(e) => setFolderId(e.target.value)} data-testid="dms-req-folder"
                className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm outline-none">
                {folderOptions(folders.filter((f) => !f.is_archive_folder)).map((o) => (
                  <option key={o.value} value={o.value} title={o.pathLabel}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">書類種別</Label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="dms-req-type"
                className="mt-1 h-9 w-full rounded-md border border-zinc-300 bg-white px-2 text-sm outline-none">
                {meta.doc_types.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">期限（任意）</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                className="h-9 mt-1" data-testid="dms-req-due" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">担当者名（任意）</Label>
              <Input value={assignee} onChange={(e) => setAssignee(e.target.value)} maxLength={100}
                className="h-9 mt-1" data-testid="dms-req-assignee" />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="dms-req-cancel-dlg">キャンセル</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="dms-req-save">
            {saving ? '作成中...' : 'リクエスト作成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
